/**
 * Catalog Admin API Client
 *
 * API client for Species and Gear Catalog Admin operations
 *
 * Epic: Super Admin Catalog Management Console
 * Stories: SCRUM-299, SCRUM-301
 */

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3030';

/**
 * Make API request with authentication
 */
async function apiRequest(endpoint, options = {}) {
  const {
    method = 'GET',
    body = null,
    params = null,
    headers = {}
  } = options;

  // Build URL with query params
  const url = new URL(`${API_BASE}${endpoint}`);
  if (params) {
    Object.keys(params).forEach(key => {
      // eslint-disable-next-line security/detect-object-injection -- safe: key is from Object.keys() iteration
      if (params[key] !== undefined && params[key] !== null) {
        // eslint-disable-next-line security/detect-object-injection -- safe: key is from Object.keys() iteration
        url.searchParams.append(key, params[key]);
      }
    });
  }

  // Get auth token from localStorage
  const token = localStorage.getItem('accessToken');

  // Build request options
  const requestOptions = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers
    },
    credentials: 'include'
  };

  // Add Authorization header if token exists
  if (token) {
    requestOptions.headers['Authorization'] = `Bearer ${token}`;
  }

  // Add body for POST/PUT/PATCH requests
  if (body && method !== 'GET' && method !== 'DELETE') {
    requestOptions.body = JSON.stringify(body);
  }

  // Make request
  const response = await fetch(url.toString(), requestOptions);

  // Handle errors
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  // Parse response
  return await response.json();
}

/**
 * Species Catalog Admin API
 */
export const speciesCatalogAdminApi = {
  /**
   * List species with filters and pagination
   */
  async listSpecies(params) {
    return await apiRequest('/api/v1/admin/species-catalog', { params });
  },

  /**
   * Get single species by ID with all relations
   */
  async getSpeciesById(speciesId) {
    return await apiRequest(`/api/v1/admin/species-catalog/${speciesId}`);
  },

  /**
   * Create new species
   */
  async createSpecies(speciesData) {
    return await apiRequest('/api/v1/admin/species-catalog', {
      method: 'POST',
      body: speciesData
    });
  },

  /**
   * Update species (partial update)
   */
  async updateSpecies(speciesId, updates) {
    return await apiRequest(`/api/v1/admin/species-catalog/${speciesId}`, {
      method: 'PATCH',
      body: updates
    });
  },

  /**
   * Delete species (soft delete - set is_active = false)
   */
  async deleteSpecies(speciesId) {
    return await apiRequest(`/api/v1/admin/species-catalog/${speciesId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Get regional presence records for species
   */
  async getRegionalPresence(speciesId) {
    return await apiRequest(`/api/v1/admin/species-catalog/${speciesId}/regional-presence`);
  },

  /**
   * Add regional presence record
   */
  async addRegionalPresence(speciesId, presenceData) {
    return await apiRequest(`/api/v1/admin/species-catalog/${speciesId}/regional-presence`, {
      method: 'POST',
      body: presenceData
    });
  },

  /**
   * Update regional presence record
   */
  async updateRegionalPresence(speciesId, presenceId, updates) {
    return await apiRequest(
      `/api/v1/admin/species-catalog/${speciesId}/regional-presence/${presenceId}`,
      {
        method: 'PUT',
        body: updates
      }
    );
  },

  /**
   * Delete regional presence record
   */
  async deleteRegionalPresence(speciesId, presenceId) {
    return await apiRequest(
      `/api/v1/admin/species-catalog/${speciesId}/regional-presence/${presenceId}`,
      {
        method: 'DELETE'
      }
    );
  },

  /**
   * Get water types for species (fish only)
   */
  async getWaterTypes(speciesId) {
    return await apiRequest(`/api/v1/admin/species-catalog/${speciesId}/water-types`);
  },

  /**
   * Add water type
   */
  async addWaterType(speciesId, waterTypeData) {
    return await apiRequest(`/api/v1/admin/species-catalog/${speciesId}/water-types`, {
      method: 'POST',
      body: waterTypeData
    });
  },

  /**
   * Delete water type (composite key)
   */
  async deleteWaterType(speciesId, waterType, waterBodyType) {
    return await apiRequest(`/api/v1/admin/species-catalog/${speciesId}/water-types`, {
      method: 'DELETE',
      body: { water_type: waterType, water_body_type: waterBodyType }
    });
  },

  /**
   * Upload image to S3
   */
  async uploadImage(speciesId, imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);

    const token = localStorage.getItem('authToken');

    const response = await fetch(
      `${API_BASE}/api/v1/admin/species-catalog/${speciesId}/upload-image`,
      {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData,
        credentials: 'include'
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Image upload failed');
    }

    return await response.json();
  },

  /**
   * Update image URL
   */
  async updateImageUrl(speciesId, imageUrl, thumbnailUrl) {
    return await apiRequest(`/api/v1/admin/species-catalog/${speciesId}/image-url`, {
      method: 'PUT',
      body: { image_url: imageUrl, thumbnail_url: thumbnailUrl }
    });
  },

  /**
   * Get statistics
   */
  async getStatistics() {
    return await apiRequest('/api/v1/admin/species-catalog/statistics');
  }
};

/**
 * Gear Catalog Admin API
 */
export const gearCatalogAdminApi = {
  /**
   * List gear with filters and pagination
   */
  async listGear(params) {
    return await apiRequest('/api/v1/admin/gear-catalog', { params });
  },

  /**
   * Get single gear by ID with all relations
   */
  async getGearById(gearId) {
    return await apiRequest(`/api/v1/admin/gear-catalog/${gearId}`);
  },

  /**
   * Create new gear
   */
  async createGear(gearData) {
    return await apiRequest('/api/v1/admin/gear-catalog', {
      method: 'POST',
      body: gearData
    });
  },

  /**
   * Update gear (partial update)
   */
  async updateGear(gearId, updates) {
    return await apiRequest(`/api/v1/admin/gear-catalog/${gearId}`, {
      method: 'PATCH',
      body: updates
    });
  },

  /**
   * Delete gear (soft delete - set is_active = false)
   */
  async deleteGear(gearId) {
    return await apiRequest(`/api/v1/admin/gear-catalog/${gearId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Get category associations for gear
   */
  async getCategoryAssociations(gearId) {
    return await apiRequest(`/api/v1/admin/gear-catalog/${gearId}/categories`);
  },

  /**
   * Add category association
   */
  async addCategoryAssociation(gearId, categoryData) {
    return await apiRequest(`/api/v1/admin/gear-catalog/${gearId}/categories`, {
      method: 'POST',
      body: categoryData
    });
  },

  /**
   * Delete category association
   */
  async deleteCategoryAssociation(gearId, associationId) {
    return await apiRequest(`/api/v1/admin/gear-catalog/${gearId}/categories/${associationId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Upload image to S3
   */
  async uploadImage(gearId, imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);

    const token = localStorage.getItem('authToken');

    const response = await fetch(
      `${API_BASE}/api/v1/admin/gear-catalog/${gearId}/upload-image`,
      {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData,
        credentials: 'include'
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Image upload failed');
    }

    return await response.json();
  },

  /**
   * Update image URL
   */
  async updateImageUrl(gearId, photoUrl) {
    return await apiRequest(`/api/v1/admin/gear-catalog/${gearId}/image-url`, {
      method: 'PUT',
      body: { photo_url: photoUrl }
    });
  },

  /**
   * Get statistics
   */
  async getStatistics() {
    return await apiRequest('/api/v1/admin/gear-catalog/statistics');
  }
};

/**
 * Audit Logs API
 */
export const auditLogApi = {
  /**
   * List audit logs with filters
   */
  async listAuditLogs(params) {
    return await apiRequest('/api/v1/admin/audit-logs', { params });
  },

  /**
   * Get audit history for specific record
   */
  async getAuditHistory(tableName, recordId) {
    return await apiRequest(`/api/v1/admin/audit-logs/${tableName}/${recordId}`);
  }
};
